import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { ApiResponse } from '@org/api-contract';
import { KEYCLOAK } from './keycloak.token';
import { environment } from '../environments/environment';
import { Identity } from './wire';
import { redirectToLogin } from './redirect-to-login';

/** Why no identity loaded. Only a 403 from `/me` means the account has no ym_users row;
 *  any other failure is an outage of sign-in or of upapi. */
export type AccessFailure = 'not-provisioned' | 'unavailable' | 'signed-out';

/** Query flag logout returns with. Keycloak logout leaves the yearbook-master session alive,
 *  so an automatic login on return would sign the same account straight back in. */
export const SIGNED_OUT = 'signed-out';

function failureOf(err: unknown): AccessFailure {
  return err instanceof HttpErrorResponse && err.status === 403
    ? 'not-provisioned'
    : 'unavailable';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly keycloak = inject(KEYCLOAK);
  private readonly http = inject(HttpClient);
  private readonly _identity = signal<Identity | null>(null);
  readonly identity = this._identity.asReadonly();
  private readonly _accessFailure = signal<AccessFailure | null>(null);
  readonly accessFailure = this._accessFailure.asReadonly();
  private identityLoad: Promise<void> | null = null;

  isInternal(): boolean {
    return !!this._identity()?.internal;
  }
  // Both terms, matching CatalogAuthoringController: EDIT_PRINT_SPEC rides a permission column a
  // client role can also hold, and the catalog has no tenant column, so staff is the real boundary.
  isCatalogAdmin(): boolean {
    return this.isInternal() && this.has('EDIT_PRINT_SPEC');
  }
  canAuthorTemplates(): boolean {
    return this.isInternal() && this.has('AUTHOR_TEMPLATES');
  }
  has(capability: Identity['capabilities'][number]): boolean {
    return this._identity()?.capabilities.includes(capability) ?? false;
  }

  logout(): void {
    // A path-bearing URL: a bare origin can fail Keycloak 26 redirect-URI wildcard matching.
    void this.keycloak.logout({
      redirectUri: `${window.location.origin}/?${SIGNED_OUT}`,
    });
  }
  signIn(): Promise<void> {
    return redirectToLogin(
      this.keycloak,
      environment.kcIdentityProvider,
      window.location,
    );
  }
  async loadIdentity(): Promise<void> {
    const res = await firstValueFrom(
      this.http.get<ApiResponse<Identity>>(`${environment.apiBase}/me`),
    );
    if (!res?.data) throw new Error('/me answered without an identity');
    this._identity.set(res.data);
    this._accessFailure.set(null);
  }

  markUnavailable(): void {
    this._accessFailure.set('unavailable');
  }
  markSignedOut(): void {
    this._accessFailure.set('signed-out');
  }

  /** Loads `/me` once; a failure is recorded on `accessFailure` and forgotten, so a later
   *  call refetches. */
  ensureIdentity(): Promise<void> {
    this.identityLoad ??= this.loadIdentity().catch((err: unknown) => {
      this._accessFailure.set(failureOf(err));
      this.identityLoad = null;
      throw err;
    });
    return this.identityLoad;
  }
}
